import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";

@customElement("bp-lookup")
export class Lookup extends LitElement {
  static styles = css`
    .lookup {
      --arrow-size: 12px;
      --min-box-width: 250px;
      --lookup-reveal-duration: var(--duration-long);
      display: inline-block;
      position: relative;
    }

    .word {
      position: relative;
    }

    .background {
      position: absolute;
      top: 0;
      bottom: 0;
      left: -3px;
      right: -3px;
      transition: background-color var(--lookup-reveal-duration);
      z-index: -1;
    }

    .box-arrow {
      position: absolute;
      border-left: var(--arrow-size) solid transparent;
      border-right: var(--arrow-size) solid transparent;
      box-shadow: 0px 4px 34px rgb(0 0 0 / 15%);
      transition:
        border-bottom-color var(--lookup-reveal-duration),
        border-top-color var(--lookup-reveal-duration);
      z-index: var(--z-20);
    }

    .anchor-bottom-left .box-arrow,
    .anchor-bottom-right .box-arrow {
      top: calc(100% - 2px);
      border-bottom: var(--arrow-size) solid transparent;
    }

    .anchor-top-left .box-arrow,
    .anchor-top-right .box-arrow {
      bottom: calc(100% - 2px);
      border-top: var(--arrow-size) solid transparent;
    }

    .anchor-bottom-left .box-arrow,
    .anchor-top-left .box-arrow {
      left: 16px;
    }

    .anchor-bottom-right .box-arrow,
    .anchor-top-right .box-arrow {
      right: 16px;
    }

    .shown .background {
      background-color: #d7ecff;
    }

    .shown .box-arrow {
      border-bottom-color: #298ae2;
    }

    .anchor-top-left.shown .box-arrow,
    .anchor-top-right.shown .box-arrow {
      border-top-color: var(--color-white);
    }

    .information-container {
      position: absolute;
      z-index: var(--z-10);
    }

    .anchor-bottom-left .information-container,
    .anchor-bottom-right .information-container {
      top: calc(100% + 10px);
    }

    .anchor-top-left .information-container,
    .anchor-top-right .information-container {
      bottom: calc(100% + 10px);
    }

    .anchor-top-left .information-container,
    .anchor-bottom-left .information-container {
      left: -4px;
    }

    .anchor-top-right .information-container,
    .anchor-bottom-right .information-container {
      right: -4px;
    }

    .information {
      background: var(--color-white);
      box-shadow: 0px 4px 34px rgb(0 0 0 / 15%);
      border-radius: 12px;
      min-width: var(--min-box-width);
      opacity: 0;
      transition: opacity var(--lookup-reveal-duration);
    }

    .shown .information {
      opacity: 1;
    }

    .top-info {
      border-radius: 12px 12px 0 0;
      background-color: #298ae2;
      padding: var(--size-2) var(--size-2-5);
    }

    .hebrew {
      font-weight: var(--font-weight-normal);
      font-size: var(--font-size-6xl);
      line-height: var(--line-height-tight);
      color: #9fd2ff;
    }

    .roman {
      font-weight: var(--font-weight-semibold);
      font-size: var(--font-size-6xl);
      line-height: var(--line-height-tight);
      color: var(--color-white);
    }

    .bottom-info {
      border-radius: 0 0 12px 12px;
      padding: var(--size-2) var(--size-2-5);
    }

    .phonetic {
      font-family: sans-serif; // Graphik messes phoenetics up
      font-weight: var(--font-weight-normal);
      font-size: var(--font-size-4xl);
      line-height: var(--line-height-tight);
      color: var(--color-black);
    }

    .num-results {
      font-weight: var(--font-weight-normal);
      font-size: var(--font-size-4xl);
      line-height: var(--line-height-tight);
      color: var(--color-neutral-50);
    }
  `;

  @property({ attribute: "anchor-position" })
  anchorPosition?: string = "bottom-left";

  @property({
    type: Boolean,
    attribute: "no-animation",
    converter: (value) => (value === "true" ? true : false),
  })
  noAnimation?: boolean = false;

  @property({
    type: Boolean,
    attribute: "show",
  })
  show?: boolean = false;

  @property({ attribute: "hebrew" })
  hebrew?: string;

  @property({ attribute: "roman" })
  roman?: string;

  @property({ attribute: "phonetic" })
  phonetic?: string;

  @property({ attribute: "num-results" })
  numResults?: string;

  render() {
    const style = this.noAnimation ? "--lookup-reveal-duration: 0;" : "";
    return html`
      <div
        style=${style}
        class="lookup ${this.show ? "shown" : "hidden"} anchor-${this
          .anchorPosition}"
      >
        <div class="background"></div>
        <span class="word"><slot></slot></span>
        <div class="box-arrow"></div>
        <div class="information-container">
          <div class="information">
            <div class="top-info">
              <bp-text-hebrew class="hebrew">${this.hebrew}</bp-text-hebrew>
              <div class="roman">${this.roman}</div>
            </div>
            <div class="bottom-info">
              <div class="phonetic">${this.phonetic}</div>
              <div class="num-results">${this.numResults} Results</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bp-lookup": Lookup;
  }
}
